import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { forkJoin, Observable, of, throwError } from "rxjs";
import { catchError, switchMap, tap, map } from "rxjs/operators";
import { Router } from "@angular/router";
import { jwtDecode } from "jwt-decode";
import { ApiUrlService } from "./api-url.service";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";
import { CacheService } from "./cache.service";

interface Role {
  role_id: number;
  role_name: string;
}

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private tokenUrl = "/token";
  private userUrl = "/api/users/me";
  private rolesUrl = "/api/roles";
  private orgUrl = "/api/organizations";
  private tokenKey = "authToken";
  private userInfoKey = "userInfo";
  private rolesKey = "userRoles";
  private orgKey = "userOrganization";

  constructor(
    private http: HttpClient,
    private router: Router,
    private apiUrlService: ApiUrlService,
    private cacheService: CacheService
  ) {}

  // Helper function to get data from Filesystem (or localStorage for web)
  private async getFilesystemItemString(key: string): Promise<string> {
    if (Capacitor.isNative) {
      try {
        const { data } = await Filesystem.readFile({
          path: key,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        return data as string;
      } catch (e) {
        console.error("Error reading from Filesystem", e);
        return null;
      }
    } else {
      return localStorage.getItem(key) || "null";
    }
  }

  private async getFilesystemItemJson(key: string): Promise<any> {
    if (Capacitor.isNative) {
      try {
        const { data } = await Filesystem.readFile({
          path: key,
          directory: Directory.Data,
          encoding: Encoding.UTF8,
        });
        return JSON.parse(data as string);
      } catch (e) {
        console.error("Error reading from Filesystem", e);
        return null;
      }
    } else {
      return JSON.parse(localStorage.getItem(key) || "null");
    }
  }

  // Helper function to set data in Filesystem (or localStorage for web)
  private async setFilesystemItem(key: string, value: any): Promise<void> {
    // Only stringify if the value is not already a string
    const dataToStore =
      typeof value === "string" ? value : JSON.stringify(value);

    if (Capacitor.isNative) {
      // 1) Write to filesystem
      await Filesystem.writeFile({
        path: key,
        data: dataToStore,
        directory: Directory.Data,
        encoding: Encoding.UTF8,
      });

      // 2) Write to Preferences
      try {
        console.log(`Saving ${key} to Preferences`);
        await Preferences.set({ key, value: dataToStore });
        console.log(`✅ Saved ${key} to Preferences`);
      } catch (err) {
        console.error(`⛔️ Preferences.set failed for key "${key}"`, err);
      }

      // 3) Legacy: write to localStorage last, but don’t let it break anything else
      try {
        localStorage.setItem(key, dataToStore);
      } catch (err) {
        console.warn(`⚠️ localStorage.setItem failed for key "${key}"`, err);
      }
    } else {
      // In a browser, still wrap localStorage in try/catch
      try {
        localStorage.setItem(key, dataToStore);
      } catch (err) {
        console.warn(`⚠️ localStorage.setItem failed for key "${key}"`, err);
      }
    }
  }

  // Helper function to remove data from Filesystem (or localStorage for web)
  private async removeFilesystemItem(key: string): Promise<void> {
    if (Capacitor.isNative) {
      localStorage.removeItem(key); // TODO keep removing here as well for legacy features, will slowly remove
      await Filesystem.deleteFile({
        path: key,
        directory: Directory.Data,
      });
    } else {
      localStorage.removeItem(key);
    }
  }

  async login(username: string, password: string): Promise<any> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      accept: "application/json",
    });

    const body = new HttpParams()
      .set("username", username)
      .set("password", password);

    const response = await this.http
      .post<any>(
        this.apiUrlService.getApiUrl() + this.tokenUrl,
        body.toString(),
        { headers }
      )
      .toPromise();

    if (response && response.access_token) {
      console.log("Storing token:", response.access_token);
      await this.storeToken(response.access_token);
    }

    console.log("Getting user info, token was just stored");
    const userInfo = await this.getUserInfo();
    await this.getRoles(); // Fetch roles and store them
    const userOrg = await this.getOrganization();

    return { userInfo, userOrg };
  }

  private storeToken(token: string): Promise<void> {
    console.log("Storing Token Original", token);
    return this.setFilesystemItem(this.tokenKey, token);
  }

  private storeUserInfo(userInfo: any): Promise<void> {
    return this.setFilesystemItem(this.userInfoKey, userInfo);
  }

  private storeRoles(roles: Role[]): Promise<void> {
    return this.setFilesystemItem(this.rolesKey, roles);
  }

  private clearUserInfo(): void {
    console.log("Clearing user info");
    this.removeFilesystemItem(this.userInfoKey);
  }

  private clearRoles(): void {
    this.removeFilesystemItem(this.rolesKey);
  }

  private clearOrganization(): void {
    this.removeFilesystemItem(this.orgKey);
  }

  async hasRolesByName(requiredRoles: string[]): Promise<boolean> {
    const roles = await this.getRoles(); // Await async result of getRoles
    const hasRequiredRoles = requiredRoles.every((requiredRole) =>
      roles.some((role) => role.role_name === requiredRole)
    );
    return hasRequiredRoles;
  }

  async hasRoles(requiredRoles: Role[]): Promise<boolean> {
    const roles = await this.getRoles(); // Await async result of getRoles
    const hasRequiredRoles = requiredRoles.every((role) =>
      roles.includes(role)
    );
    return hasRequiredRoles;
  }

  async getToken(): Promise<string | null> {
    try {
      const token = await this.getFilesystemItemString(this.tokenKey); // Await the async operation to get the token
      if (this.isTokenExpired(token)) {
        this.logout(); // If expired, logout immediately
        return null; // Return null to indicate no valid token
      } else {
        return token; // Return the token if it's valid
      }
    } catch (error) {
      console.error("Error retrieving token:", error);
      this.logout(); // If there's an error, logout
      return null; // Return null if there's an error
    }
  }

  async isLoggedIn(): Promise<boolean> {
    const token = await this.getToken(); // Await the async result of getToken
    return !!token;
  }

  async logout(): Promise<void> {
    this.sendLogoutMessage();
    this.router.navigate(["/login"]);

    // 2. Give other frames / your React Native webview a moment to handle it
    await new Promise((res) => setTimeout(res, 20));

    // 3. Now clear all stored data across web and native
    await this.clearAllAppStorage();
  }

  // Wipe everything we control: CacheService, Capacitor Preferences, Filesystem Data files,
  // browser storages (localStorage/sessionStorage) and Cache Storage (if available).
  private async clearAllAppStorage(): Promise<void> {
    try {
      // Clear CacheService-managed resources (web: localStorage; native: Filesystem Cache dir)
      if (
        this.cacheService &&
        typeof this.cacheService.clearAllResources === "function"
      ) {
        await this.cacheService.clearAllResources();
      }

      // Clear Capacitor Preferences (native)
      try {
        await Preferences.clear();
      } catch (e) {
        console.warn("Preferences.clear failed", e);
      }

      // Clear Filesystem Data directory entries created by this service (native)
      if (Capacitor.isNative) {
        try {
          // Attempt to delete known keys first
          await Promise.all([
            this.removeFilesystemItem(this.tokenKey),
            this.removeFilesystemItem(this.userInfoKey),
            this.removeFilesystemItem(this.rolesKey),
            this.removeFilesystemItem(this.orgKey),
          ]);
        } catch (e) {
          console.warn("Failed removing known data files", e);
        }

        // Best-effort: iterate Directory.Data and delete any files we can see under root
        try {
          const list = await Filesystem.readdir({
            path: "",
            directory: Directory.Data,
          });
          if ((list as any)?.files?.length) {
            for (const f of (list as any).files) {
              try {
                await Filesystem.deleteFile({
                  path: f.name || f,
                  directory: Directory.Data,
                });
              } catch (e) {
                // ignore per-file errors
              }
            }
          }
        } catch (e) {
          // Some platforms may not allow listing Data root; ignore
        }
      }

      // Clear browser storages
      try {
        localStorage.clear();
      } catch {}
      try {
        sessionStorage.clear();
      } catch {}

      // Clear Cache Storage (PWA/browser) if available
      try {
        const globalAny: any =
          typeof self !== "undefined"
            ? self
            : typeof window !== "undefined"
            ? (window as any)
            : undefined;
        if (
          globalAny &&
          globalAny.caches &&
          typeof globalAny.caches.keys === "function"
        ) {
          const names: string[] = await globalAny.caches.keys();
          await Promise.all(
            names.map((n: string) => globalAny.caches.delete(n))
          );
        }
      } catch (e) {
        console.warn("Clearing Cache Storage failed", e);
      }
    } catch (e) {
      console.error("Failed to clear app storage", e);
    }
  }

  public sendLogoutMessage(): void {
    console.log("self:", self as any);
    console.log("self.clients:", (self as any).clients);

    if (typeof window !== "undefined") {
      console.log("Sending logout message to window:", window);
      window.postMessage({ type: "logout" }, "*");
    }

    if (typeof self !== "undefined") {
      console.log("Sending logout message to self:", self);
      (self as any).postMessage({ type: "logout" }, "*");
    }

    if (typeof self !== "undefined" && (self as any).clients) {
      (self as any).clients.matchAll().then((clients: any[]) => {
        clients.forEach((client: any) => {
          console.log("Sending logout message to client:", client);
          client.postMessage({ type: "logout" });
        });
      });
    } else {
      console.warn("self.clients is not available.");
    }
  }

  async resetPassword(userEmail: string): Promise<any> {
    const resetPasswordUrl = `/api/users/${userEmail}/forgot-password`;
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      accept: "application/json",
    });

    return this.http
      .post<any>(this.apiUrlService.getApiUrl() + resetPasswordUrl, null, {
        headers,
      })
      .toPromise();
  }

  async getUserInfo(): Promise<any> {
    console.log("Inside getUserInfo");
    const storedUserInfo = await this.getFilesystemItemJson(this.userInfoKey);
    if (storedUserInfo) {
      console.log("Returning stored user info");
      return storedUserInfo;
    }

    console.log("No stored user info, fetching from server");
    const token = await this.getToken();
    if (!token) {
      this.router.navigate(["/login"]);
      throw new Error("No token found");
    }

    const url = this.apiUrlService.getApiUrl() + this.userUrl;
    console.log("Token:", token);
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    console.log("Fetching user info from server", url);

    console.log("Bearer token being sent:", token);
    console.log("Authorization header:", headers.get("Authorization"));
    headers
      .keys()
      .forEach((key) => console.log(`Header ${key}:`, headers.get(key)));

    return new Promise((resolve, reject) => {
      this.http.get<any>(url, { headers }).subscribe({
        next: (response) => {
          console.log("Raw response data:", response);

          // If your API ever wraps data in .source, unwrap here
          let userInfo = response;

          console.log("Final userInfo object:", userInfo);
          this.storeUserInfo(userInfo)
            .then(() => resolve(userInfo))
            .catch((e) => {
              console.error("Error storing user info:", e);
              resolve(userInfo); // still resolve with fetched data
            });
        },
        error: (err) => {
          console.error("Error fetching user info:", err);
          if (err.status === 404) {
            this.logout();
          }
          reject(err);
        },
      });
    });
  }

  async getRoles(): Promise<Role[]> {
    // First, attempt to fetch the roles from the filesystem
    const storedRoles = await this.getFilesystemItemJson(this.rolesKey);
    if (storedRoles) {
      console.log("Returning roles from Filesystem");
      return storedRoles; // Return cached roles from Filesystem
    }

    // If no roles in cache, fetch the token
    const token = await this.getToken(); // Await async result of getToken
    if (!token) {
      this.router.navigate(["/login"]); // Redirect to login if no token
      throw new Error("No token found"); // Throw error if no token
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    console.log("Headers:", headers.get("Authorization"));

    try {
      // Await the result of getUserInfo() instead of using observable
      const userInfo = await this.getUserInfo(); // Await the async result of getUserInfo
      console.log("UserInfo received:", userInfo);

      const userRoles = userInfo.roles ? userInfo.roles : []; // Get role_ids from userInfo

      // Fetch all roles (with role_id and role_name) from the server
      const rolesData = await this.http
        .get<Role[]>(`${this.apiUrlService.getApiUrl()}${this.rolesUrl}`, {
          headers,
        })
        .toPromise(); // Await the HTTP request

      console.log("Roles data received:", rolesData);

      // Map user role_ids to their corresponding { role_id, role_name }
      const userRoleDetails: Role[] = userRoles.map((roleId) => {
        const matchingRole = rolesData.find((role) => role.role_id === roleId);
        return matchingRole
          ? matchingRole
          : { role_id: roleId, role_name: "Unknown" };
      });

      // Optionally store the role details in Filesystem
      this.storeRoles(userRoleDetails);

      // Return the array of role objects with both role_id and role_name
      return userRoleDetails;
    } catch (error) {
      console.error("Error fetching roles:", error);
      throw new Error("Failed to fetch roles");
    }
  }

  decodeToken(token: string): any {
    try {
      return jwtDecode(token);
    } catch (Error) {
      return null;
    }
  }

  getTokenExpirationDate(token: string): Date | null {
    const decoded: any = this.decodeToken(token);

    if (decoded === null || !decoded.hasOwnProperty("exp")) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  isTokenExpired(token: string): boolean {
    const expirationDate = this.getTokenExpirationDate(token);
    return expirationDate === null
      ? true
      : !(expirationDate.valueOf() > new Date().valueOf());
  }

  async getOrganization(): Promise<any> {
    // First, attempt to fetch the organization from the filesystem
    const storedOrganization = await this.getFilesystemItemJson(this.orgKey);
    if (storedOrganization) {
      console.log("Returning organization from Filesystem");
      return storedOrganization; // Return cached organization from Filesystem
    }

    // If no organization in cache, fetch the token
    const token = await this.getToken(); // Await async result of getToken
    if (!token) {
      this.router.navigate(["/login"]); // Redirect to login if no token
      throw new Error("No token found"); // Instead of returning throwError, throw the error
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });

    console.log("Headers:", headers.get("Authorization"));

    try {
      // Await the result of getUserInfo() instead of using observable
      const userInfo = await this.getUserInfo(); // Await the async result of getUserInfo
      console.log("UserInfo received:", userInfo);

      const organizationId = userInfo.organization_id; // Extract organization_id from userInfo

      // Check if the organization ID is present
      if (!organizationId) {
        console.error("No organization ID found in user info");
        throw new Error("No organization ID found");
      }

      // Fetch the organization details using the organization_id
      const organizationData = await this.http
        .get<any>(
          `${this.apiUrlService.getApiUrl()}${this.orgUrl}/${organizationId}`,
          { headers }
        )
        .toPromise(); // Await the HTTP request

      console.log("Organization data received:", organizationData);

      // Optionally store the organization details in Filesystem
      this.setFilesystemItem(this.orgKey, organizationData);

      // Return the organization details
      return organizationData;
    } catch (error) {
      console.error("Error fetching organization:", error);
      throw new Error("Failed to fetch organization");
    }
  }
}

// curl -X 'GET' \
//   'https://api.medicnowsolutions.com/api/users/me' \
//   -H 'accept: application/json' \
//   -H 'Authorization: Bearer eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ0ZXN0QHRlc3QuY29tIiwiZXhwIjoxNzQ4OTc0NzUwfQ.Vm6EHYw6LJdh0zM4gc6SN_bSXlSe9hT_WoVokqLl7XKS_NyVoDyH12zcMzmgALvUvEZnq6-BnZ0t9rwbGYVEETEsenmonhuT0QA0bGndSf0JtATd6QRgI3gpqs8puFYpFU2CbEDNU1TxixZ8T3WYRXmeLNJWCo5oxjH2ZBPzOgo81RRJQ8zQwgOaY7FmyUC_B8P9MKdVEseiRff_Y366G2EQBhSAtzMYxj1D66CJme8UKG417joiRFCuBNzCqrDnzVXbgriZolvQjY4rCJj6AySm4VqHROtS-SDmaYSRzyA4Rl4wOBy7Y3r8Bw32Vb4yjY174KrRz4O6v7cy1Ej6pg'
